import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { getUserById } from '../api/user';
import { createCheckoutSession } from '../api/stripe';
import { UserSubscriptionStatus } from '../types/user.types';

interface TrialInfo {
  daysRemaining: number;
  endDate: Date | null;
}

const TrialStatus: React.FC = () => {
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<UserSubscriptionStatus | null>(null);
  const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userId = localStorage.getItem('userId');
        if (userId) {
          const user = await getUserById();
          setSubscriptionStatus(user.subscriptionStatus);

          if (user.subscriptionStatus === UserSubscriptionStatus.TRIAL) {
            let daysRemaining = 0;
            let endDate: Date | null = null;

            if (user.subscription?.trialEndDate) {
              endDate = new Date(user.subscription.trialEndDate);
              daysRemaining = Math.ceil(
                (endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
              );
            } else {
              const createdAt = new Date(user.createdAt);
              endDate = new Date(createdAt);
              endDate.setDate(endDate.getDate() + 7);
              daysRemaining = Math.ceil(
                (endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
              );
            }

            setTrialInfo({
              daysRemaining: Math.max(0, daysRemaining),
              endDate,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();

    const interval = setInterval(fetchUserData, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const priceId = import.meta.env.VITE_STRIPE_PRICE_ID;
      if (!priceId) {
        throw new Error('Price ID not configured');
      }

      const session = await createCheckoutSession(priceId);
      if (session.url) {
        window.location.href = session.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Only show for trial users
  if (subscriptionStatus !== UserSubscriptionStatus.TRIAL || !showBanner) {
    return null;
  }

  const isExpiringSoon = trialInfo && trialInfo.daysRemaining <= 3;

  return (
    <div className="fixed top-4 right-4 z-50">
      <div
        className={`
          relative overflow-hidden rounded-lg shadow-lg max-w-sm
          ${
            isExpiringSoon
              ? 'bg-gradient-to-r from-red to-orange'
              : 'bg-gradient-to-r from-violet to-violet-light'
          }
        `}
      >
        {/* Background pattern for visual interest */}
        <div className="absolute inset-0 bg-black bg-opacity-10"></div>

        <div className="relative px-4 py-3">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0 mt-0.5">
              {isExpiringSoon ? (
                <ExclamationTriangleIcon className="w-5 h-5 text-white" />
              ) : (
                <ClockIcon className="w-5 h-5 text-white" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm leading-tight">
                {trialInfo && (
                  <>
                    {isExpiringSoon ? (
                      <span>
                        Trial expires in {trialInfo.daysRemaining}{' '}
                        {trialInfo.daysRemaining === 1 ? 'day' : 'days'}!
                      </span>
                    ) : (
                      <span>
                        {trialInfo.daysRemaining}{' '}
                        {trialInfo.daysRemaining === 1 ? 'day' : 'days'} left in
                        trial
                      </span>
                    )}
                  </>
                )}
              </p>

              {/* Action buttons */}
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={handleUpgrade}
                  disabled={isLoading}
                  className={`
                    px-3 py-1 rounded-full text-xs font-medium transition-all
                    ${
                      isExpiringSoon
                        ? 'bg-white text-red hover:bg-gray-100'
                        : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                    }
                    ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {isLoading ? 'Loading...' : 'Upgrade'}
                </button>

                <button
                  onClick={() => setShowBanner(false)}
                  className="text-white hover:text-white hover:opacity-80 transition-opacity p-1"
                  aria-label="Close banner"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialStatus;
